import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Smartphone, Tv, Lock } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import SearchableDropdown from '../SearchableDropdown';

import { RubyGemMark } from '../RubyGemMark';
/**
 * Edit Kiosk modal.
 *
 * Editable: name, description, orientation, screen size, resolution,
 *           idle playlist, idle delay, resume window, allowBuildYourOwn.
 * Locked  : provisionCode, kioskType, locationId.
 *           (Want to change location or type? Delete + re-provision.)
 *
 * No code-rotation flow ("New code") — per user decision, the provision code
 * stays bound to the kiosk for its lifetime; a stolen/replaced tablet should
 * be Deleted and re-provisioned.
 */

interface Kiosk {
  kioskId: string;
  tenantId: string;
  locationId: string;
  kioskType: string;
  name: string;
  description?: string;
  provisionCode?: string;
  // Loose strings here (KiosksTab uses `string`) — local state narrows on read.
  orientation?: string;
  resolution?: string;
  screenSizeInches?: number;
  idlePlaylistId?: string | null;
  idleAfterSeconds?: number;
  resumeWindowMinutes?: number;
  config?: Record<string, unknown>;
}

interface Props {
  isOpen: boolean;
  kiosk: Kiosk | null;
  tenantId: string;
  locationName?: string;
  onClose: () => void;
  onSaved: () => void;
}

const SCREEN_SIZE_PRESETS = [
  { value: 7, label: '7″ small tablet / phone' },
  { value: 8, label: '8″ tablet' },
  { value: 10.9, label: '10.9″ iPad / Android tablet' },
  { value: 11, label: '11″ iPad Pro' },
  { value: 12.9, label: '12.9″ iPad Pro' },
  { value: 13, label: '13″ tablet / laptop' },
  { value: 24, label: '24″ monitor' },
  { value: 32, label: '32″ display / TV' },
  { value: 43, label: '43″ TV' },
  { value: 55, label: '55″ TV' },
  { value: 65, label: '65″ TV' },
];

interface PlaylistOption { playlistId: string; name: string }

const EditKioskModal: React.FC<Props> = ({ isOpen, kiosk, tenantId, locationName, onClose, onSaved }) => {
  // Editable state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('portrait');
  const [screenSizeInches, setScreenSizeInches] = useState<number | ''>('');
  const [resolution, setResolution] = useState<'tablet' | '1080p' | '4k'>('tablet');
  const [idlePlaylistId, setIdlePlaylistId] = useState<string>('');
  const [idleAfterSeconds, setIdleAfterSeconds] = useState(60);
  const [resumeWindowMinutes, setResumeWindowMinutes] = useState(5);
  const [allowBuildYourOwn, setAllowBuildYourOwn] = useState(true);

  const [playlists, setPlaylists] = useState<PlaylistOption[]>([]);
  const [saving, setSaving] = useState(false);

  // Seed from the kiosk record whenever the modal opens or the source changes.
  useEffect(() => {
    if (!isOpen || !kiosk) return;
    setName(kiosk.name || '');
    setDescription(kiosk.description || '');
    setOrientation((kiosk.orientation === 'landscape' ? 'landscape' : 'portrait'));
    setScreenSizeInches(typeof kiosk.screenSizeInches === 'number' ? kiosk.screenSizeInches : '');
    const r = kiosk.resolution;
    setResolution(r === '1080p' || r === '4k' ? r : 'tablet');
    setIdlePlaylistId(kiosk.idlePlaylistId || '');
    setIdleAfterSeconds(kiosk.idleAfterSeconds ?? 60);
    setResumeWindowMinutes(kiosk.resumeWindowMinutes ?? 5);
    const cfg = kiosk.config || {};
    if (kiosk.kioskType === 'oil-blending') {
      setAllowBuildYourOwn(
        typeof (cfg as any).allowBuildYourOwn === 'boolean' ? (cfg as any).allowBuildYourOwn : true,
      );
    }
    // Playlists lookup deferred; backend accepts null → branded fallback loop.
    setPlaylists([]);
  }, [isOpen, kiosk]);

  const canSubmit = useMemo(() => !!name.trim() && !saving, [name, saving]);

  const handleSave = useCallback(async () => {
    if (!kiosk || !canSubmit) return;
    setSaving(true);
    try {
      const patch: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim() || undefined,
        orientation,
        screenSizeInches: typeof screenSizeInches === 'number' ? screenSizeInches : undefined,
        resolution,
        idlePlaylistId: idlePlaylistId || null,
        idleAfterSeconds,
        resumeWindowMinutes,
      };
      if (kiosk.kioskType === 'oil-blending') {
        patch.config = { allowBuildYourOwn };
      }
      await api.kiosks.patch(kiosk.kioskId, tenantId, patch);
      toastService.success('Kiosk updated');
      onSaved();
      onClose();
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to update kiosk');
    } finally {
      setSaving(false);
    }
  }, [
    kiosk, canSubmit, name, description, orientation, screenSizeInches, resolution,
    idlePlaylistId, idleAfterSeconds, resumeWindowMinutes, allowBuildYourOwn, tenantId,
    onSaved, onClose,
  ]);

  if (!isOpen || !kiosk) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white text-stone-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-black text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <div>
            <h2 className="text-base md:text-lg font-semibold">Edit Kiosk</h2>
            <div className="text-xs text-stone-300 mt-0.5">{kiosk.name}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Locked fields */}
          <Section title="Identity (locked)">
            <LockedField label="Provisioning code" value={kiosk.provisionCode || '—'} mono />
            <LockedField label="Location" value={locationName || kiosk.locationId} />
            <LockedField label="Kiosk type" value={kiosk.kioskType} />
            <p className="text-xs text-stone-500 mt-1">
              To change location, type, or code: delete this kiosk and provision a new one.
            </p>
          </Section>

          {/* Editable: identity */}
          <Section title="Name">
            <FieldRow label="Display label">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 text-sm text-stone-900 bg-white border border-stone-300 rounded focus:outline-none focus:border-emerald-600"
              />
            </FieldRow>
            <FieldRow label="Description">
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional"
                className="w-full px-3 py-2 text-sm text-stone-900 bg-white border border-stone-300 rounded focus:outline-none focus:border-emerald-600 placeholder:text-stone-400"
              />
            </FieldRow>
          </Section>

          {/* Display */}
          <Section title="Display">
            <FieldRow label="Orientation">
              <div className="grid grid-cols-2 gap-2">
                <OrientationBtn
                  active={orientation === 'portrait'}
                  onClick={() => setOrientation('portrait')}
                  icon={<Smartphone size={20} />}
                  label="Portrait"
                />
                <OrientationBtn
                  active={orientation === 'landscape'}
                  onClick={() => setOrientation('landscape')}
                  icon={<Tv size={20} />}
                  label="Landscape"
                />
              </div>
            </FieldRow>
            <FieldRow label="Screen size">
              <SearchableDropdown
                options={SCREEN_SIZE_PRESETS.map((s) => ({ value: String(s.value), label: s.label }))}
                value={screenSizeInches === '' ? null : String(screenSizeInches)}
                onChange={(v) => setScreenSizeInches(v === null || v === '' ? '' : parseFloat(v))}
                placeholder="Not set"
              />
            </FieldRow>
            <FieldRow label="Resolution">
              <SearchableDropdown
                options={[
                  { value: 'tablet', label: 'Tablet (native)' },
                  { value: '1080p', label: '1080p' },
                  { value: '4k', label: '4K' },
                ]}
                value={resolution}
                onChange={(v) => setResolution((v || 'tablet') as any)}
                placeholder="Pick resolution"
              />
            </FieldRow>
          </Section>

          {/* Idle */}
          <Section title="Idle Mode">
            <FieldRow label="Idle playlist" hint="Plays when no touch">
              <SearchableDropdown
                options={[
                  { value: '__none', label: 'None (branded fallback loop)' },
                  ...playlists.map((p) => ({ value: p.playlistId, label: p.name })),
                ]}
                value={idlePlaylistId || '__none'}
                onChange={(v) => setIdlePlaylistId(v === '__none' || v === null ? '' : v)}
                placeholder="Pick a playlist"
              />
            </FieldRow>
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label={`Idle delay: ${idleAfterSeconds}s`}>
                <input
                  type="range" min={15} max={300} step={5}
                  value={idleAfterSeconds}
                  onChange={(e) => setIdleAfterSeconds(parseInt(e.target.value, 10))}
                  className="w-full"
                />
              </FieldRow>
              <FieldRow label={`Resume window: ${resumeWindowMinutes}min`}>
                <input
                  type="range" min={0} max={30} step={1}
                  value={resumeWindowMinutes}
                  onChange={(e) => setResumeWindowMinutes(parseInt(e.target.value, 10))}
                  className="w-full"
                />
              </FieldRow>
            </div>
          </Section>

          {/* Per-type: oil-blending */}
          {kiosk.kioskType === 'oil-blending' && (
            <Section title="Oil Blending Settings">
              <label className="flex items-center gap-2 text-sm text-stone-700 pt-1">
                <input
                  type="checkbox"
                  checked={allowBuildYourOwn}
                  onChange={(e) => setAllowBuildYourOwn(e.target.checked)}
                  className="w-4 h-4 accent-emerald-600"
                />
                Allow customers to build their own blend
              </label>
            </Section>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded text-sm font-medium text-stone-600 hover:bg-stone-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSubmit}
              className="bg-black text-white px-4 py-2 rounded text-sm font-medium hover:bg-stone-800 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {saving && <RubyGemMark size={14} drawing color="currentColor" />}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── presentational helpers (kept local) ─────────────────── */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">{title}</div>
    <div className="space-y-2">{children}</div>
  </div>
);

const FieldRow: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="block">
    <div className="flex items-baseline justify-between mb-1">
      <span className="text-sm text-stone-700">{label}</span>
      {hint && <span className="text-xs text-stone-400">{hint}</span>}
    </div>
    {children}
  </label>
);

const LockedField: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded px-3 py-2">
    <span className="text-xs text-stone-500">{label}</span>
    <span className={`text-sm text-stone-700 inline-flex items-center gap-1 ${mono ? 'font-mono' : ''}`}>
      {value}
      <Lock size={11} className="text-stone-400" />
    </span>
  </div>
);

const OrientationBtn: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex flex-col items-center justify-center gap-1 py-3 rounded-lg border transition ${
      active ? 'border-emerald-600 bg-emerald-50 text-emerald-700' : 'border-stone-300 hover:border-stone-400 text-stone-600'
    }`}
  >
    {icon}
    <span className="text-sm font-medium">{label}</span>
  </button>
);

export default EditKioskModal;
